import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColDef } from 'ag-grid-community';
import Lottie from 'react-lottie';

import tableLottie from '~/shared/assets/lotties/table.json';

import TablePagination from './TablePagination';

export default function Table<T>({
  rowData,
  columnDefs,
  defaultColDef,
  pagination,
  onSortChange,
  headerHeight,
}: {
  rowData?: T[];
  columnDefs: ColDef<T>[];
  defaultColDef?: ColDef<T>;
  pagination?: {
    perPage: number;
    onChange: (num: number) => void;
    totalPages: number;
  };
  onSortChange?: (sort: { field: string; dir: 'asc' | 'desc' }) => void;
  headerHeight?: number;
}) {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const apiRef = useRef<AgGridReact>(null);

  function onGridReady() {
    apiRef?.current?.api.sizeColumnsToFit();
  }

  const { perPage, onChange: onPageChange, totalPages } = pagination || {};

  useEffect(() => setCurrentPage(0), [totalPages]);

  const goToPage = useCallback(
    (page: number) => {
      onPageChange?.(page);
      setCurrentPage(page);
    },
    [setCurrentPage, onPageChange]
  );

  const rows = useMemo(() => rowData, [rowData]);
  const columns = useMemo(() => columnDefs, [columnDefs]);

  return (
    <div className="flex-1 flex flex-col gap-[28px] relative">
      <main className="ag-theme-alpine flex-1 relative">
        <AgGridReact
          ref={apiRef}
          rowData={rows}
          columnDefs={columns}
          className="w-[100%] h-[100%]"
          onGridReady={onGridReady}
          sortingOrder={['asc', 'desc']}
          suppressMultiSort
          animateRows={false}
          pagination={!!pagination}
          paginationPageSize={perPage}
          headerHeight={headerHeight}
          defaultColDef={{
            sortable: true,
            icons: {
              sortAscending: '<p class="ag-icon-tree-open" style="transform: rotate(180deg);"/>',
              sortDescending: '<p class="ag-icon-tree-open" style=""/>',
            },
            ...defaultColDef,
          }}
          suppressPaginationPanel={!!pagination}
          loadingOverlayComponent={LoadingOverlay}
          noRowsOverlayComponent={NoRowsOverlay}
          onSortChanged={(s) => {
            const v = s.api.getColumnState().find((c) => !!c.sort);
            onSortChange?.({ field: v!.colId, dir: v!.sort! });
          }}
          suppressScrollOnNewData
          alwaysShowHorizontalScroll={true}
          alwaysShowVerticalScroll={true}
        />
      </main>
      {totalPages ? (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          goToPage={(num) => {
            goToPage(num);
            apiRef?.current?.api.ensureIndexVisible(0);
          }}
        />
      ) : null}
    </div>
  );
}

function LoadingOverlay() {
  return (
    <div
      className="absolute top-[88px] left-0 h-[calc(100%_-_88px)] flex justify-center items-center bg-white"
      aria-label="loading"
    >
      <Lottie
        options={{
          loop: true,
          autoplay: true,
          animationData: tableLottie,
          rendererSettings: {
            preserveAspectRatio: 'none',
          },
        }}
        height={356}
        width={1552}
      />
    </div>
  );
}

function NoRowsOverlay() {
  return <div>No Results</div>;
}
